import { useContext } from "react";
import { AppUtils } from "../../../services/AppUtils";
import { ValidationUtils } from "../../../services/ValidationUtils";
import EnrollmentContext from "../../../contexts/EnrollUserContext";
import useEnrollment from "../../../services/useEnrollment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const SwalAlert = withReactContent(Swal);
const NON_DEFAULT_REASON_WARNING =
  "Please note, your non default co applicant will not be approved if the relevant document for reason for opting a non default co applicant is not uploaded and hence they will not be eligible for any kind of benefits that are meant for co applicants. Are you sure that you want to go ahead without uploading the document for reason for opting a non default co applicant?";
const RELATIONSHIP_PROOF_WARNING =
  "Please note, your co applicant will be approved only if the document for proving the relationship is uploaded and they will be eligible for any kind of benefits that are meant for co applicants only after the approval. Are you sure that you want to go ahead without uploading the relationship document?";
const UNDERTAKING_REMARKS =
  "Please note that the co applicant will be eligible for the benefits only if the uploaded documents justify your respective selections and get approved. Any further changes once submitted may be chargeable and completely at the discretion of the company.";

export const useValidatePage = () => {
  const { enrollmentDetails, isPageDirty, setPageError, currentPage, setCurrentPage, setEnrollmentDetails, linkID } = useContext(EnrollmentContext);
  const { checkPAN, checkEmail, checkPhone, saveEnrolleeDetails } = useEnrollment(1);

  const isUploadedFile = (value) => typeof File !== "undefined" && value instanceof File;
  const hasDocument = (fileValue, documentName, documentUrl) => Boolean(fileValue || documentName || documentUrl);

  const showSkipUploadPopup = async (text, confirmButtonText, cancelButtonText) => {
    const result = await SwalAlert.fire({
      icon: "warning",
      title: "",
      text,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      reverseButtons: true,
      allowOutsideClick: false,
    });

    return result.isConfirmed;
  };

  const showUndertakingPopup = async () => {
    const result = await SwalAlert.fire({
      icon: "info",
      title: "Undertaking",
      html: `<div class="text-left"><p>${UNDERTAKING_REMARKS}</p></div>`,
      showCancelButton: true,
      confirmButtonText: "Agree",
      cancelButtonText: "Go back and modify",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    return result.isConfirmed;
  };

  return async (navigateToPage = -1) => {
    let pageIsValid = true;
    setPageError(null);
    if (isPageDirty) {
      switch (currentPage) {
        case 0:
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.basicDetails?.first_name, "Please enter your first name", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.basicDetails?.second_name, "Please enter your last name", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.validateDOB(enrollmentDetails.basicDetails?.dob, "Please enter date of birth", setPageError);
          pageIsValid =
            pageIsValid &&
            (!ValidationUtils.isEmpty(enrollmentDetails.basicDetails?.aadhar_no, "Kindly enter a valid Aadhar Number", () => {}) ||
              ValidationUtils.isValid(enrollmentDetails.basicDetails?.aadhar_no, /^[2-9]\d{11}$/, "Kindly enter a valid Aadhar Number", setPageError));
          pageIsValid =
            pageIsValid &&
            (!ValidationUtils.isEmpty(enrollmentDetails.basicDetails?.pan_no, "Kindly enter a valid PAN Number", () => {}) ||
              ValidationUtils.isValid(enrollmentDetails.basicDetails?.pan_no, /^[A-Z]{5}\d{4}[A-Z]$/, "Kindly enter a valid PAN Number", setPageError));
          pageIsValid =
            pageIsValid &&
            (!ValidationUtils.isEmpty(enrollmentDetails.basicDetails?.gst_no, "Kindly enter a valid GST Number", () => {}) ||
              ValidationUtils.isValid(
                enrollmentDetails.basicDetails?.gst_no,
                /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[A-Z0-9]{1}$/,
                "Kindly enter a valid GST Number",
                setPageError
              ));
          if (
            (enrollmentDetails.basicDetails.gst_no === "" && enrollmentDetails.basicDetails.gst_declaration === 0) ||
            (enrollmentDetails.basicDetails.pan_no === "" && enrollmentDetails.basicDetails.pan_declaration === 0)
          ) {
            console.log("reached");
            AppUtils.showDialog("dlgConsent");
            pageIsValid = false;
          }
          if (pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.basicDetails?.pan_no, "Kindly enter a valid PAN Number", () => {})) {
            let panCheckResult;
            await checkPAN(
              enrollmentDetails.basicDetails.pan_no,
              (data) => {
                if (data?.code === 200) {
                  panCheckResult = true;
                  const nameParts = data?.data?.full_name.split(" ");
                  const firstName = nameParts[0];
                  const lastName = nameParts.slice(1).join(" ");
                  setEnrollmentDetails({
                    ...enrollmentDetails,
                    basicDetails: { ...enrollmentDetails.basicDetails, first_name: firstName, second_name: lastName },
                  });
                }
              },
              (error) => {
                setPageError(error);
                panCheckResult = false;
              }
            );
            pageIsValid = pageIsValid && panCheckResult;
          }
          break;

        case 1:
          pageIsValid = pageIsValid && ValidationUtils.isValid(enrollmentDetails.contactDetails?.phone, /^[6-9]\d{9}$/, "Please enter a valid phone number", setPageError);
          pageIsValid =
            pageIsValid &&
            ValidationUtils.isValid(enrollmentDetails.contactDetails?.email, /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/, "Please enter a valid email", setPageError);
          pageIsValid =
            pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.contactDetails?.permanent_address_line1, "Please enter your permanent address - line 1", setPageError);
          pageIsValid =
            pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.contactDetails?.permanent_address_line2, "Please enter your permanent address - line 2", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.contactDetails?.permanent_postalcode_id.toString(), "Please enter pincode", setPageError);
          if (pageIsValid) {
            let phoneCheckResult;
            let emailCheckResult;
            await checkPhone(
              enrollmentDetails.contactDetails?.phone,
              () => {
                phoneCheckResult = true;
              },
              (error) => {
                setPageError(error);
                phoneCheckResult = false;
              }
            );
            await checkEmail(
              enrollmentDetails.contactDetails?.email,
              () => {
                emailCheckResult = true;
              },
              (error) => {
                setPageError(error);
                emailCheckResult = false;
              }
            );
            pageIsValid = pageIsValid && phoneCheckResult && emailCheckResult;
          }
          break;

        case 2:
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.bankDetails?.account_name, "Please enter your account name", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.bankDetails?.account_no, "Please enter your account number", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.bankDetails?.bank_name, "Please select your bank by entering a valid IFSC code", setPageError);
          pageIsValid =
            pageIsValid &&
            ValidationUtils.isSame(enrollmentDetails.bankDetails?.account_no, enrollmentDetails.bankDetails?.confirm_account_no, "Account numbers don't match", setPageError);
          break;

        case 3:
          pageIsValid =
            pageIsValid &&
            ValidationUtils.isEmpty(String(enrollmentDetails.coApplicantDetails?.coapplicant_relationship_id ?? ""), "Select co-applicant relationship", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.isEmpty(enrollmentDetails.coApplicantDetails?.coapplicant_name, "Please enter co-applicant name", setPageError);
          pageIsValid = pageIsValid && ValidationUtils.validateDOB(enrollmentDetails.coApplicantDetails?.coapplicant_dob, "Please enter co-applicant date of birth", setPageError);
          pageIsValid =
            pageIsValid && ValidationUtils.isValid(enrollmentDetails.coApplicantDetails?.coapplicant_phone, /^[6-9]\d{9}$/, "Enter a valid 10 digit phone number", setPageError);
          if (pageIsValid && Number(enrollmentDetails.coApplicantDetails?.is_default_coapplicant) === 0) {
            pageIsValid =
              pageIsValid &&
              ValidationUtils.isEmpty(
                (enrollmentDetails.coApplicantDetails?.non_default_coapp_reason ?? "").trim(),
                "Please enter reason for non-default co-applicant",
                setPageError
              );
            pageIsValid =
              pageIsValid &&
              ValidationUtils.customCondition(
                (enrollmentDetails.coApplicantDetails?.non_default_coapp_reason ?? "").length <= 500,
                "Reason for non-default co-applicant cannot exceed 500 characters",
                setPageError
              );
          }

          if (pageIsValid && navigateToPage > currentPage) {
            const coApplicantDetails = enrollmentDetails.coApplicantDetails ?? {};
            const hasNonDefaultReasonDocument = hasDocument(
              coApplicantDetails?.non_default_reason_file,
              coApplicantDetails?.non_default_coapp_reason_doc_name,
              coApplicantDetails?.non_default_coapp_reason_doc_url
            );
            const hasRelationshipProofDocument = hasDocument(
              coApplicantDetails?.coapp_relationship_proof_file,
              coApplicantDetails?.coapp_relationship_proof_doc_name,
              coApplicantDetails?.coapp_relationship_proof_url
            );
            const hasUploadedSupportingDocument =
              isUploadedFile(coApplicantDetails?.non_default_reason_file) || isUploadedFile(coApplicantDetails?.coapp_relationship_proof_file);

            if (Number(coApplicantDetails?.is_default_coapplicant) === 0 && !hasNonDefaultReasonDocument) {
              pageIsValid = await showSkipUploadPopup(NON_DEFAULT_REASON_WARNING, "Yes", "No, I will upload now");
            }

            if (pageIsValid && !hasRelationshipProofDocument) {
              pageIsValid = await showSkipUploadPopup(RELATIONSHIP_PROOF_WARNING, "Yes", "No, I will upload now");
            }

            if (pageIsValid && hasUploadedSupportingDocument) {
              pageIsValid = await showUndertakingPopup();
            }
          }
          break;

        case 4:
          pageIsValid = true;
          break;

        default:
          pageIsValid = true;
          break;
      }
    }

    if (pageIsValid === true && navigateToPage > -1) {
      saveEnrolleeDetails(linkID, currentPage, enrollmentDetails, () => {
        setPageError(null);
        setCurrentPage(navigateToPage);
      });
    }
  };
};
